import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helpers — token may live in localStorage (Remember Me ON) or sessionStorage (OFF).
const getStoredToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');
const setStoredToken = (token, rememberMe) => {
  // Always clear both first to avoid stale dual entries
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
  if (rememberMe) {
    localStorage.setItem('token', token);
  } else {
    sessionStorage.setItem('token', token);
  }
};
const clearStoredToken = () => {
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
  localStorage.removeItem('originalToken');
  localStorage.removeItem('originalUserName');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getStoredToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = getStoredToken();
      if (storedToken) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          clearStoredToken();
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password, remember_me: rememberMe });
    const { access_token, user: userData } = response.data;
    setStoredToken(access_token, rememberMe);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const signup = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/signup`, userData);
    const { access_token, user: newUser } = response.data;
    setStoredToken(access_token, false);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    clearStoredToken();
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const forgotPassword = async (email) => {
    await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
  };

  const verifyOTP = async (email, otp) => {
    const response = await axios.post(`${API_URL}/api/auth/verify-otp`, { email, otp });
    return response.data;
  };

  const resetPassword = async (email, otp, newPassword) => {
    await axios.post(`${API_URL}/api/auth/reset-password`, { email, otp, new_password: newPassword });
  };

  const changePassword = async (currentPassword, newPassword) => {
    await axios.post(`${API_URL}/api/auth/change-password`, 
      { current_password: currentPassword, new_password: newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      signup,
      logout,
      updateUser,
      forgotPassword,
      verifyOTP,
      resetPassword,
      changePassword,
      isAuthenticated: !!token && !!user,
      isSuperAdmin: user?.role === 'Super Admin',
      isAdmin: user?.role === 'Admin' || user?.role === 'Super Admin',
      isCoordinator: user?.role === 'Coordinator' || user?.role === 'Admin' || user?.role === 'Super Admin',
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
